import { capitalCase } from 'change-case';
import { Enum } from '../../../platform/Enum';
import { togglePropertyStripMode } from '../../canvas/commands/ManipulationCommands';
import { LockablePropertyStripLayout } from '../../canvas/properties/LockablePropertyStripLayout';
import { PropertyStripLayout } from '../../canvas/properties/PropertyStripLayout';
import {
    fontColorSelector,
    fontFamilySelector,
    fontSizeSelector,
    lockAndMoreLayout,
    textAlignHSelector,
    textAlignVSelector
} from '../../canvas/properties/TextPropertyStripParts';
import { connectorCreateFromSelection } from '../connector/ConnectorCommands';
import { Shape } from './Shape';
import { ShapeStyle } from './ShapeEnums';
import { ShapeKindWidget } from './ShapeKindWidget';

const shapeStyles = [
    ShapeStyle.fill,
    ShapeStyle.stroke,
    ShapeStyle.outline,
    ShapeStyle.dash,
];

const shapeStyleSelector = PropertyStripLayout<Shape>((d, s, o, w, a) => {

    const identifyImage = (forceStyle?: ShapeStyle) => {
        const style = forceStyle ?? a.get('style', { useFirstIfVarying: true });
        const variant = Enum.label(ShapeStyle, style)?.toLowerCase();
        return (!!variant) ? `deqo://icons/s2/rectangle-${variant}` : 'deqo://icons/d2/rectangle-fill';
    };

    const strip = w.createButtonStrip({
        image: identifyImage(),
        text: 'Shape Style',
        layout: 'row',
    });

    const items = shapeStyles.map(val =>
        w.createButton({
            image: identifyImage(val),
            text: capitalCase(Enum.label(ShapeStyle, val)),
            onAction() {
                o.update(() => a.set('style', val));
                strip.update({ image: identifyImage() });
            }
        })
    );

    strip.update({ items });
    return [strip];

});

export const shapePropertyLayout = LockablePropertyStripLayout<Shape>((d, s, o, w, a) => [
    //
    // Edit Text
    w.createButton({
        name: 'editText',
        image: 'deqo://icons/i8/text',
        text: 'Edit Text',
        onAction() {
            d(togglePropertyStripMode);
        },
    }),
    //
    // Divider
    w.createDivide(),
    //
    // ShapeKind
    w.createPopout({
        name: 'shapeKind',
        image: 'deqo://icons/d2/shapes',
        text: 'Shape Kind',
        ui: ShapeKindWidget,
        facade: a,
    }),
    ...shapeStyleSelector(d, s, o, w, a),
    // Background
    w.createColorSelector({
        autoSelect: true,
        groups: true,
        value: a.get('background'),
        text: 'Shape Color',
        onSelect() {
            o.update(u => {
                u.rolling(a.keyFor('background'));
                a.set('background', this.value!);
            });
        }
    }),
    //
    // CornerRadius
    w.createSlider({
        value: a.get('cornerRadius'),
        image: 'deqo://icons/i8/rounded-corner',
        text: 'Corner Radius',
        onSelect() {
            o.update(() => a.set('cornerRadius', this.value));
        }
    }),
    //
    // Stroke
    w.createSlider({
        value: a.get('stroke'),
        min: 1,
        image: 'deqo://icons/i8/line-width',
        text: 'Stroke Width',
        onSelect() {
            o.update(() => a.set('stroke', this.value));
        }
    }),
    w.createButton({
        image: 'deqo://icons/d2/connector',
        text: 'Connect To...',
        onAction() {
            d(connectorCreateFromSelection);
        }
    }),
    //
    // Divider
    w.createDivide(),
    //
    // Container
    w.createButton({
        active: !!a.get('container'),
        image: 'deqo://icons/i8/box',
        text: 'Container',
        onAction() {
            o.update(() => a.set('container', x => !x));
            this.update({ active: !!a.get('container') });
        }
    }),
    ...lockAndMoreLayout(d, s, o, w, a),
]);

export const shapeTextPropertyLayout = PropertyStripLayout<Shape>((d, s, o, w, a) => [
    //
    // Edit Text
    w.createButton({
        name: 'back',
        image: 'deqo://icons/hero/chevron-left',
        text: 'Back',
        onAction() {
            d(togglePropertyStripMode);
        },
    }),
    //
    // Divider
    w.createDivide(),
    //
    // Text Settings
    ...fontColorSelector()(d, s, o, w, a),
    ...fontFamilySelector()(d, s, o, w, a),
    ...fontSizeSelector()(d, s, o, w, a),
    ...textAlignHSelector()(d, s, o, w, a),
    ...textAlignVSelector()(d, s, o, w, a),
]);
